import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from '.';
import { User, LoginCredentials, RegisterCredentials } from '../types';
import { API_BASE_URL } from '../config';

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_BASE_URL}/auth`,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Custom base query with error handling for 401/token expiration
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Check if the error is 401 or related to token expiration
  if (result.error && result.error.status === 401) {
    // Clear authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to login page
    window.location.href = '/login';
  }

  return result;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    login: builder.mutation<{ token: string; user: User }, LoginCredentials>({
      query: (credentials) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    register: builder.mutation<void, RegisterCredentials>({
      query: (credentials) => ({
        url: '/register',
        method: 'POST',
        body: credentials,
      }),
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: (payload) => ({
        url: '/forgot-password',
        method: 'POST',
        body: payload,
      }),
    }),
    resetPassword: builder.mutation<void, { password: string; token: string }>({
      query: (payload) => ({
        url: '/reset-password',
        method: 'POST',
        body: payload,
      }),
    }),
    getMe: builder.query<User, void>({
      query: () => '/me',
    }),
    validateInvite: builder.query<{ email: string; name?: string; designation?: string; role: 'admin' | 'user'; expiresAt: string }, string>({
      query: (token) => `/invite/${token}`,
    }),
    acceptInvite: builder.mutation<{ message: string; email: string }, { token: string; name?: string; designation?: string; password: string }>({
      query: (body) => ({
        url: '/accept-invite',
        method: 'POST',
        body,
      }),
    }),
    updateMe: builder.mutation<User, { name?: string; designation?: string }>({
      query: (body) => ({
        url: '/me',
        method: 'PATCH',
        body,
      }),
    }),
    changePassword: builder.mutation<{ message: string }, { currentPassword: string; newPassword: string }>({
      query: (body) => ({
        url: '/change-password',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetMeQuery,
  useValidateInviteQuery,
  useAcceptInviteMutation,
  useUpdateMeMutation,
  useChangePasswordMutation,
} = authApi;
